import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Save, Loader2, Tag } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

export default function NewsCategoriesAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/news-categories").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.name?.trim()) return toast.error("Le nom est obligatoire");
    setBusy(true);
    try {
      if (editing.id) await api.put(`/admin/news-categories/${editing.id}`, editing);
      else await api.post("/admin/news-categories", editing);
      toast.success("Catégorie enregistrée");
      setEditing(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!confirm("Supprimer cette catégorie ? Les articles existants garderont leur libellé.")) return;
    await api.delete(`/admin/news-categories/${id}`);
    toast.success("Catégorie supprimée");
    load();
  };

  return (
    <div data-testid="news-categories-admin">
      <div className="mb-5 flex items-center justify-between">
        <div>
          <div className="eyebrow text-gold-dark">Portail</div>
          <p className="text-sm text-foreground/60 mt-1">{items.length} catégorie(s) actives — utilisées dans le module Actualités.</p>
        </div>
        <button onClick={() => setEditing({ name: "", slug: "", description: "", order: items.length })}
                className="btn-primary" data-testid="cat-create">
          <Plus className="w-4 h-4" /> Nouvelle catégorie
        </button>
      </div>

      <div className="bg-white border border-border rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-bone text-foreground/70 text-[0.65rem] uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2.5 font-semibold">Nom</th>
              <th className="text-left px-4 py-2.5 font-semibold">Slug</th>
              <th className="text-left px-4 py-2.5 font-semibold">Ordre</th>
              <th className="text-left px-4 py-2.5 font-semibold">Description</th>
              <th className="text-left px-4 py-2.5 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-t border-border" data-testid={`cat-row-${c.id}`}>
                <td className="px-4 py-2.5 font-semibold text-ink flex items-center gap-2">
                  <Tag className="w-3.5 h-3.5 text-gold-dark" /> {c.name}
                </td>
                <td className="px-4 py-2.5 text-xs font-mono text-foreground/60">{c.slug}</td>
                <td className="px-4 py-2.5 text-xs">{c.order ?? 0}</td>
                <td className="px-4 py-2.5 text-xs text-foreground/60 max-w-sm truncate">{c.description || "—"}</td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <button onClick={() => setEditing(c)} className="p-1.5 text-foreground/50 hover:text-ink" data-testid={`cat-edit-${c.id}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => del(c.id)} className="p-1.5 text-foreground/50 hover:text-crimson" data-testid={`cat-del-${c.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-foreground/55">Aucune catégorie.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-[80] grid place-items-center p-4" onClick={() => setEditing(null)}>
          <div className="absolute inset-0 bg-ink/70 backdrop-blur-sm" />
          <div className="relative bg-white w-full max-w-lg rounded-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <h3 className="font-serif text-lg text-ink">{editing.id ? "Modifier la catégorie" : "Nouvelle catégorie"}</h3>
              <button onClick={() => setEditing(null)}><X className="w-5 h-5 text-foreground/60" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-[0.7rem] uppercase tracking-[0.16em] font-semibold text-foreground/60 mb-1.5">Nom *</label>
                <input value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                       className="inp" data-testid="cat-name" placeholder="Ex : Communiqués officiels" />
              </div>
              <div>
                <label className="block text-[0.7rem] uppercase tracking-[0.16em] font-semibold text-foreground/60 mb-1.5">Slug (URL)</label>
                <input value={editing.slug || ""} onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
                       className="inp font-mono text-xs" data-testid="cat-slug" placeholder="Généré automatiquement si vide" />
              </div>
              <div>
                <label className="block text-[0.7rem] uppercase tracking-[0.16em] font-semibold text-foreground/60 mb-1.5">Description (optionnelle)</label>
                <textarea rows={2} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                          className="inp" data-testid="cat-desc" />
              </div>
              <div>
                <label className="block text-[0.7rem] uppercase tracking-[0.16em] font-semibold text-foreground/60 mb-1.5">Ordre d'affichage</label>
                <input type="number" value={editing.order ?? 0} onChange={(e) => setEditing({ ...editing, order: Number(e.target.value) })}
                       className="inp w-24" data-testid="cat-order" />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-border flex items-center justify-end gap-2">
              <button onClick={() => setEditing(null)} className="btn-outline text-sm">Annuler</button>
              <button onClick={save} disabled={busy} className="btn-primary text-sm" data-testid="cat-save">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
